import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Modal, Button, Row, Col, Badge, Table, Spinner } from "react-bootstrap";
import FeatherIcon from "feather-icons-react";

import { _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import { showToast } from "../../services/ToastService";
import { useBusiness } from "../../contexts/BusinessContext";
import BookingRoutePreviewMap from "../components/BookingRoutePreviewMap";

// --- Typings strictly matching your optimized deep payload ---
export interface BusinessItemDetails {
    id: number;
    title: string;
    duration?: string;
    price: number;
}

export interface BookingItem {
    id: number;
    booking_id: number;
    business_item_id: number;
    quantity: number;
    price: string;
    business_item?: BusinessItemDetails | null;
}

export interface BookingData {
    id: number;
    business_id: number;
    user_id: number | null;
    user: {
        id: number,
        name: string;
        email: string;
        mobile_no: string;
    } | null;
    customer_name: string;
    mobile_no: string;
    email: string | null;
    booking_type: "instant" | "scheduled";
    location_type: "center" | "home";
    scheduled_at: string;
    technician_preference: string;
    notes: string | null;
    address: string | null;
    latitude: string | number | null;
    longitude: string | number | null;
    status: number;
    created_at: string;
    updated_at: string;
    booking_items?: BookingItem[];
    total_payable?: number;
}

export interface BookingViewModalRef {
    open: (bookingOrId: number | BookingData) => void;
    close: () => void;
}

export const BookingViewModal = forwardRef<BookingViewModalRef, {}>((_, ref) => {
    const { business } = useBusiness();
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [booking, setBooking] = useState<BookingData | null>(null);
    const [liveDistance, setLiveDistance] = useState<string>("Calculating...");

    useImperativeHandle(ref, () => ({
        open: async (bookingOrId) => {
            setShow(true);
            const bookingId = typeof bookingOrId === "object" ? bookingOrId.id : bookingOrId;

            if (typeof bookingOrId === "object") {
                setBooking(bookingOrId);
            }

            await fetchFullBookingDetails(bookingId);
        },
        close: handleClose,
    }));

    const handleClose = () => {
        setShow(false);
        setBooking(null);
        setLoading(false);
        setLiveDistance("Calculating...");
    };

    const fetchFullBookingDetails = async (id: number) => {
        try {
            setLoading(true);
            const response = await _post(`${apiUrls.getBookingDetails}`, { booking_id: id });
            const responseData = response.data?.success !== undefined ? response.data?.data : response?.data;
            if (responseData) {
                setBooking(responseData);
            }
        } catch (err) {
            console.error("Error reading backend single booking record:", err);
            showToast({ icon: "error", title: "Load Error", text: "Could not fetch deep booking details." });
        } finally {
            setLoading(false);
        }
    };

    const statuses = ["Pending", "Confirmed", "Completed", "Cancelled"];
    const colors = ["warning text-dark", "primary", "success", "danger"];

    const shouldShowMap =
        booking &&
        (booking.location_type === "home" || booking.booking_type === "instant") &&
        booking.latitude &&
        booking.longitude &&
        business?.latitude &&
        business?.longitude;

    async function makeOrderFromBooking(id: number | undefined): Promise<void> {
        if (!id) {
            showToast({ icon: "error", title: "Invalid", text: "No booking selected." });
            return;
        }

        try {
            setLoading(true);
            const endpoint = apiUrls.createOrderFromBooking;
            const resp = await _post(`${endpoint}`, { booking_id: id });
            const data = resp.data?.success !== undefined ? resp.data?.data : resp?.data;

            if (data) {
                showToast({ icon: "success", title: "Order Created", text: "Order created from booking successfully." });
                handleClose();
                // if backend returned created order id, navigate to it
                const orderId = data.id || data.order_id;
                // if (orderId) {
                //     window.location.href = `/orders/${orderId}`;
                // }
            } else {
                showToast({ icon: "error", title: "Failed", text: "Could not create order from booking." });
            }
        } catch (err) {
            console.error("Error creating order from booking:", err);
            showToast({ icon: "error", title: "Server Error", text: "Could not create order. Try again." });
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            {show && (
                <div
                    onClick={handleClose}
                    style={{
                        position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                        backgroundColor: "rgba(0, 0, 0, 0.4)", zIndex: 1040, backdropFilter: "blur(1px)"
                    }}
                />
            )}

            <Modal
                show={show}
                onHide={handleClose}
                centered
                size="lg"
                container={document.body}
                style={{
                    position: "fixed", top: "50%", left: "50%",
                    transform: show ? "translate(-50%, -50%)" : "translate(-50%, -60%)",
                    zIndex: 1050, display: show ? "block" : "none", width: "100%", maxWidth: "800px"
                }}
            >
                <div className="modal-content shadow-lg border-0 bg-white rounded-3 overflow-hidden">
                    <Modal.Header className="bg-light border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                        <Modal.Title className="h5 fw-bold mb-0 text-dark d-flex align-items-center">
                            <FeatherIcon icon="file-text" size={18} className="me-2 text-primary" />
                            Booking Details #{booking?.id || ""}
                        </Modal.Title>
                        <button
                            type="button"
                            className="btn-close shadow-none m-0 p-2"
                            aria-label="Close"
                            onClick={handleClose}
                        />
                    </Modal.Header>

                    <Modal.Body className="p-4" style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
                        {booking ? (
                            <>
                                {/* Section 1: Client vs Timeline Row */}
                                <Row className="g-4 text-start mb-4">
                                    <Col md={6} className="border-end border-light">
                                        <h5 className="text-uppercase tracking-wider text-primary fw-bold small mb-3">Client Profile</h5>

                                        <div className="mb-3">
                                            <small className="text-muted d-block data-field-label">Customer Name</small>
                                            <span className="fw-bold text-dark fs-6">{booking.customer_name}</span>
                                        </div>

                                        <div className="mb-3">
                                            <small className="text-muted d-block data-field-label">Mobile Number</small>
                                            <span className="text-dark fw-medium">{booking.mobile_no || "N/A"}</span>
                                        </div>

                                        <div className="mb-0">
                                            <small className="text-muted d-block data-field-label">Email Address</small>
                                            <span className="text-muted small">{booking.email || "N/A"}</span>
                                        </div>
                                    </Col>

                                    <Col md={6}>
                                        <h5 className="text-uppercase tracking-wider text-primary fw-bold small mb-3">Service Timeline</h5>

                                        <div className="mb-3">
                                            <small className="text-muted d-block data-field-label">Scheduled Date & Time</small>
                                            <span className="text-dark fw-bold">
                                                {booking.scheduled_at
                                                    ? new Date(booking.scheduled_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                                                    : "N/A"}
                                            </span>
                                        </div>

                                        <Row>
                                            <Col xs={6} className="mb-3">
                                                <small className="text-muted d-block data-field-label">Booking Type</small>
                                                <Badge bg={booking.booking_type === 'instant' ? 'danger' : 'info'} className="text-uppercase px-2 py-1">
                                                    {booking.booking_type}
                                                </Badge>
                                            </Col>
                                            <Col xs={6} className="mb-3">
                                                <small className="text-muted d-block data-field-label">Location Type</small>
                                                <Badge bg={booking.location_type === 'home' ? 'dark' : 'secondary'} className="text-uppercase px-2 py-1">
                                                    {booking.location_type === 'home' ? '🏠 Home' : '🏢 Center'}
                                                </Badge>
                                            </Col>
                                        </Row>

                                        <div className="mb-0">
                                            <small className="text-muted d-block data-field-label">Current Status</small>
                                            <Badge bg={colors[booking.status] || 'secondary'} className="px-2 py-1">
                                                {statuses[booking.status] || "Unknown"}
                                            </Badge>
                                        </div>
                                    </Col>

                                    {booking?.user && (
                                        <Col xs={12}>
                                            <div className="p-3 bg-light rounded border">
                                                <h5 className="text-secondary fw-bold mb-2">Requested By</h5>
                                                <p className="mb-0 text-dark small" style={{ whiteSpace: "pre-line" }}>
                                                    Name: {booking?.user?.name}, 
                                                    Mobile No: {booking?.user?.mobile_no}, 
                                                    Email: {booking?.user?.email}
                                                </p>
                                            </div>
                                        </Col>
                                    )}

                                </Row>

                                <hr className="border-light my-4" />

                                {/* Section 2: Items Table / Job Specifications */}
                                <div className="text-start mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="text-uppercase tracking-wider text-primary fw-bold small mb-0">Requested Services</h5>
                                        {loading && <Spinner animation="border" size="sm" variant="primary" />}
                                    </div>

                                    {booking.booking_items && booking.booking_items.length > 0 ? (
                                        <Table responsive bordered hover className="align-middle bg-white rounded mb-0">
                                            <thead className="table-light small text-uppercase">
                                                <tr>
                                                    <th>Service Title</th>
                                                    <th className="text-center">Quantity</th>
                                                    <th className="text-end">Unit Price</th>
                                                    <th className="text-end">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {booking.booking_items.map((item) => (
                                                    <tr key={item.id} className="small">
                                                        <td className="fw-bold text-dark">
                                                            {item.business_item?.title || `Service Asset #${item.business_item_id}`}
                                                        </td>
                                                        <td className="text-center fw-bold">{item.quantity}</td>
                                                        <td className="text-end">{parseFloat(item.price).toFixed(2)} BDT</td>
                                                        <td className="text-end fw-bold text-primary">
                                                            {(parseFloat(item.price) * item.quantity).toFixed(2)} BDT
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr className="table-light fw-bold fs-6">
                                                    <td colSpan={3} className="text-end text-uppercase small">Total Account Payable:</td>
                                                    <td className="text-end text-primary">
                                                        {booking.total_payable
                                                            ? `${parseFloat(booking.total_payable as any).toFixed(2)} BDT`
                                                            : `${booking.booking_items.reduce((sum, i) => sum + (parseFloat(i.price) * i.quantity), 0).toFixed(2)} BDT`}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    ) : !loading ? (
                                        <div className="p-3 bg-light rounded text-muted small text-center">No structural services linked to this layout item.</div>
                                    ) : (
                                        <div className="p-4 text-center text-muted small"><Spinner animation="grow" size="sm" className="me-2" />Loading items layout...</div>
                                    )}
                                </div>

                                {/* Section 3: Notes Container */}
                                {booking.notes && (
                                    <div className="text-start mb-4 p-3 bg-light border rounded">
                                        <small className="text-muted d-block data-field-label">Customer Instructions / Notes</small>
                                        <p className="mb-0 text-dark small style-preserve-whitespace">{booking.notes}</p>
                                    </div>
                                )}

                                {/* Section 4: Address Details & Map Preview Block with Custom Overlay Styling */}
                                {shouldShowMap && (
                                    <div className="text-start border-top pt-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h5 className="text-uppercase tracking-wider text-primary fw-bold small mb-0">Delivery & Track Location</h5>
                                            <Badge bg="success" className="px-2 py-1 text-uppercase tracking-wider">
                                                📍 Live Track
                                            </Badge>
                                        </div>

                                        {booking.address && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block data-field-label">Service Destination Address</small>
                                                <span className="text-dark small fw-medium">{booking.address}</span>
                                            </div>
                                        )}

                                        {/* Maps wrapper with overlay elements matching your screenshot flavor */}
                                        <div className="rounded overflow-hidden border shadow-sm position-relative">
                                            <BookingRoutePreviewMap
                                                businessLocation={{
                                                    lat: Number(business?.latitude),
                                                    lng: Number(business?.longitude),
                                                    name: business?.name || "Center Hub"
                                                }}
                                                customerLocation={{
                                                    lat: Number(booking.latitude),
                                                    lng: Number(booking.longitude),
                                                    address: booking.address || ""
                                                }}
                                                onDistanceCalculated={(d: any) => setLiveDistance(d)}
                                            />

                                            {/* Bottom Floating Control Bar (Screenshot layout alignment) */}
                                            <div
                                                className="d-flex justify-content-between align-items-center p-3 bg-white border-top w-100"
                                                style={{ position: "relative", zIndex: 1010 }}
                                            >
                                                <p className="mb-0 small text-dark">
                                                    Distance: <span className="fw-bold text-primary fs-6">{liveDistance}</span>
                                                </p>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="fw-semibold px-3 shadow-sm text-uppercase tracking-wider btn-sm"
                                                    style={{ fontSize: '0.78rem' }}
                                                    onClick={() =>
                                                        window.open(
                                                            `https://www.google.com/maps/dir/?api=1&origin=${business?.latitude},${business?.longitude}&destination=${booking.latitude},${booking.longitude}`,
                                                            "_blank"
                                                        )
                                                    }
                                                >
                                                    Get Directions
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-5 my-3 text-muted">
                                <Spinner animation="border" className="mb-3 d-block mx-auto" variant="primary" />
                                Mapping structure data model context...
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer className="border-top px-4 py-3 bg-light d-flex justify-content-end mb-0">
                        <Button variant="outline-secondary" className="px-4 btn-md fw-bold" onClick={handleClose}>
                            Dismiss
                        </Button>
                        <Button variant="primary" className="px-4 btn-md fw-bold ms-2" onClick={() => makeOrderFromBooking(booking?.id)}>
                            Accept
                        </Button>
                    </Modal.Footer>
                </div>
            </Modal>

            <style>{`
        .data-field-label {
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 3px;
        }
        .style-preserve-whitespace {
          white-space: pre-wrap;
        }
      `}</style>
        </>
    );
});

BookingViewModal.displayName = "BookingViewModal";